import React, { useEffect, useState } from "react";
import { useQuery } from '../helpers/useQuery';
import authProvider, { AuthenticateError } from '../helpers/authProvider';
import { useHistory } from 'react-router';
import Spinner from './Spinner';
import LogoPage from './LogoPage';
import { resendLinkPath } from '../helpers/paths';
import axios from 'axios';
import { Link } from "react-router-dom";
import { MagicLinkRequest } from '../models/dto/magicLinkRequest';
import './MagicLinkPage.scss';

export default function MagicLinkPage() {
	const query = useQuery();
	const id = query.get('id');
	const sig = query.get('sig');
	const [state, setState] = useState<State>(id && sig ? 'authenticating' : 'missing');
	const [resendingLink, setResendingLink] = useState<boolean>(false);
	const [linkResent, setLinkResent] = useState<boolean>(false);
	const history = useHistory();
	
	useEffect(() => {
		let destroyed = false;
		
		if (id && sig) {
			authProvider.authenticate(id, sig).then(() => {
				// when authenticated, the app should auto redirect
				// to the home page - so this code shouldn't run if 
				// everything is working as expected - so this is just
				// a fallback
				if (!destroyed) {
					setState('success');
				}
			}, (error: AuthenticateError) => {
				if (error.type === 'linkSent') {
					setState('linkSent');
				} else if (error.type === 'requestError' && error.error.response?.status === 410) {
					setState('expired');
				} else {
					history.push(`${resendLinkPath}?error=true`);
				}
			})
		}
		
		return () => { 
			destroyed = true;
		};
	}, []);
	
	return (
		<LogoPage>
			<React.Fragment>
				{state === 'authenticating' && <Spinner noDelay={true} />}
				{state === 'success' &&
					<div className="text-center">
						<div className="text-bold mb-2">Successfully authenticated!</div>
						<div><Link to="/">Click here</Link> to continue if not redirected automatically.</div>
					</div>
				}
				{state === 'missing' && 
					<React.Fragment>
						<div className="text-red text-center">This access link is invalid.</div>
						<div className="text-center mt-2"><Link to={resendLinkPath}>Send a new link</Link></div>
					</React.Fragment>
				}
				{state === 'linkSent' && 
					<div className="text-center">
						<h3 className="h3-mb-1">A new link has been emailed to you</h3>
						<div className="magic-link-page__link-sent-subtext">This link has expired, so we’ve emailed you a new link you can use to access the upload portal.</div>
					</div>
				}
				{state === 'expired' && 
					<div className="text-center">
						<h1 className="h1-mb-1">This link has expired</h1>
						<div className="mb-3">
							{!linkResent && <button className="btn" disabled={resendingLink} onClick={resendLink}>Email me a new link</button>}
							{linkResent && <span>A link has been sent</span>}
						</div>
						<div><Link to={resendLinkPath}>Use a different email address</Link></div>
					</div>
				}
			</React.Fragment>
		</LogoPage>
	);
	
	function resendLink(): void {
		if (resendingLink || !id || !sig) return;
		
		setResendingLink(true);
		
		const request: MagicLinkRequest = { id, signature: sig };
		axios.post('/api/token/send-link-token', request).then(() => {
			setLinkResent(true);
			setResendingLink(false);
		}, () => {
			setResendingLink(false);
		});
	}
}

type State = 'authenticating' | 'missing' | 'linkSent' | 'expired' | 'success';